import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtCurrency } from "@/lib/format";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

const CLOSED_STATES = ["PRE", "PREPRE", "POST", "POSTPOST", "CLOSED"];
const NUDGE_KEY = "yahooExpiryNudgeAt";
const NUDGE_INTERVAL = 30 * 60 * 1000; // re-remind every 30 min while expired

function computeStatus(data) {
    const auth = data.auth || {};
    const age = data.age_seconds;
    const ms = data.market_state;

    if (auth.configured && !auth.logged_in) {
        return { color: "bg-red-500", label: "Session expired", detail: "Your Yahoo cookies are no longer valid — reconnect in Settings to restore live data.", reconnect: true };
    }
    if (!auth.configured) {
        return { color: "bg-amber-500", label: "Delayed ~15m", detail: "No Yahoo session connected. Connect your subscriber cookies in Settings for real-time quotes.", reconnect: true };
    }
    if (ms && CLOSED_STATES.includes(ms)) {
        return { color: "bg-muted-foreground", label: "Market closed", detail: "US market is closed. Live freshness resumes at the next session." };
    }
    if (age == null) {
        return { color: "bg-muted-foreground", label: "Connected", detail: "Session active. Freshness reading unavailable right now." };
    }
    if (age < 120) return { color: "bg-emerald-500", label: `Live · ${age}s`, detail: `Latest quote is ${age}s old — essentially real-time.` };
    if (age < 900) return { color: "bg-amber-500", label: `${Math.round(age / 60)}m delayed`, detail: `Latest quote is ~${Math.round(age / 60)} min old.` };
    return { color: "bg-amber-500", label: "Delayed", detail: `Latest quote is ~${Math.round(age / 60)} min old — data may be delayed.` };
}

export function DataFreshnessBadge() {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { user } = useAuth();
    const [refreshing, setRefreshing] = useState(false);
    const { data, refetch } = useQuery({
        queryKey: ["freshness"],
        queryFn: async () => (await api.get("/market/freshness", { params: { ticker: "AAPL" } })).data,
        refetchInterval: 60000,
        refetchOnWindowFocus: true,
        staleTime: 55000,
    });

    const refreshAll = async () => {
        if (refreshing) return;
        setRefreshing(true);
        toast.info("Refreshing market data…");
        try {
            await api.post("/market/refresh");
            await refetch();
            await qc.invalidateQueries();
            toast.success("Data refreshed", { duration: 1500 });
        } catch {
            toast.error("Refresh failed");
        } finally {
            setTimeout(() => setRefreshing(false), 500);
        }
    };

    // Proactive nudge when the Yahoo session expires — don't rely on the badge alone.
    const prevExpired = useRef(null);
    useEffect(() => {
        if (!data) return;
        const auth = data.auth || {};
        const expired = !!(auth.configured && !auth.logged_in);
        const was = prevExpired.current;
        prevExpired.current = expired;

        if (expired) {
            const now = Date.now();
            const last = Number(localStorage.getItem(NUDGE_KEY) || 0);
            const transitioned = was === false;
            if (transitioned || now - last > NUDGE_INTERVAL) {
                localStorage.setItem(NUDGE_KEY, String(now));
                if (user?.role === "admin") {
                    toast.error("Yahoo Finance session expired", {
                        description: "Live market data is paused. Reconnect your Yahoo cookies to resume real-time quotes.",
                        duration: 12000,
                        action: { label: "Reconnect", onClick: () => navigate("/settings") },
                    });
                } else {
                    toast.warning("Market data may be delayed", {
                        description: "The Yahoo session needs a refresh — ask your admin to reconnect it.",
                        duration: 10000,
                    });
                }
            }
        } else if (auth.logged_in && was === true) {
            localStorage.removeItem(NUDGE_KEY);
            toast.success("Yahoo session reconnected — live data resumed");
        }
    }, [data, user, navigate]);

    if (!data) return null;
    const st = computeStatus(data);
    const auth = data.auth || {};

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    onClick={refreshAll}
                    className="flex items-center gap-1.5 px-2 py-1 rounded-full border border-border hover:bg-muted/50 transition-colors text-xs font-medium"
                    data-testid="freshness-badge"
                    title="Click to refresh all data">
                    {refreshing
                        ? <RefreshCw className="w-3 h-3 animate-spin text-primary" data-testid="freshness-refreshing" />
                        : <span className={`w-2 h-2 rounded-full ${st.color} ${st.color === "bg-emerald-500" ? "animate-pulse" : ""}`} data-testid="freshness-dot" />}
                    <span className="hidden sm:inline num" data-testid="freshness-label">{st.label}</span>
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-0" data-testid="freshness-popover">
                <div className="px-4 py-3 border-b border-border">
                    <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${st.color}`} />
                        <span className="font-heading font-semibold text-sm">{st.label}</span>
                        {auth.tier && <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground capitalize">Yahoo {auth.tier}</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">{st.detail}</p>
                </div>
                <div className="px-4 py-3 text-xs space-y-1 text-muted-foreground">
                    {data.last_price != null && <div>Ref quote (AAPL): <span className="num text-foreground">{fmtCurrency(data.last_price, { decimals: 2 })}</span></div>}
                    {data.age_seconds != null && <div>Data age: <span className="text-foreground">{data.age_seconds < 120 ? `${data.age_seconds}s` : `${Math.round(data.age_seconds / 60)} min`}</span></div>}
                    {data.market_state && <div>Market state: <span className="text-foreground">{data.market_state}</span></div>}
                </div>
                <div className="px-4 pb-3 space-y-2">
                    <Button variant="outline" size="sm" className="w-full" onClick={refreshAll} disabled={refreshing} data-testid="freshness-refresh-btn">
                        <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />Refresh all data
                    </Button>
                    {st.reconnect && user?.role === "admin" && (
                        <Button size="sm" className="w-full" onClick={() => navigate("/settings")} data-testid="freshness-reconnect-btn">Open Settings to reconnect</Button>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}
